'use client';

import Head from 'next/head';
import { Prose } from 'components/elements/Prose';
import { environment } from 'config/global';
import {
  LEGAL_LOCALE_META,
  type LegalContentLocale,
  type LegalDocument,
  type LegalSlug,
} from 'core/legal';

type Props = {
  doc: LegalDocument;
  slug: LegalSlug;
  /** The content locale being shown (drives canonical + active alternate). */
  contentLocale: LegalContentLocale;
  /** BCP-47 tag for date formatting (the resolved app locale). */
  localeTag: string;
  /** Locales this slug is actually published in (for hreflang alternates). */
  availableLocales: LegalContentLocale[];
};

const origin = environment.origin?.replace(/\/$/, '') ?? '';

const formatDate = (iso: string, localeTag: string) =>
  new Intl.DateTimeFormat(localeTag, { dateStyle: 'long' }).format(
    new Date(`${iso}T00:00:00`),
  );

export const LegalPageLayout = ({
  doc,
  slug,
  contentLocale,
  localeTag,
  availableLocales,
}: Props) => {
  const { frontmatter, html, toc } = doc;
  const canonical = `${origin}${LEGAL_LOCALE_META[contentLocale].prefix}/legal/${slug}`;

  return (
    <div className="pt-16 font-inter text-mf-off-black-950">
      <Head>
        <title>{`${frontmatter.title} · MindFeed`}</title>
        <meta name="description" content={frontmatter.description} />
        <link rel="canonical" href={canonical} />
        {availableLocales.map((loc) => (
          <link
            key={loc}
            rel="alternate"
            hrefLang={LEGAL_LOCALE_META[loc].hreflang}
            href={`${origin}${LEGAL_LOCALE_META[loc].prefix}/legal/${slug}`}
          />
        ))}
        {availableLocales.includes('en') && (
          <link
            rel="alternate"
            hrefLang="x-default"
            href={`${origin}/legal/${slug}`}
          />
        )}
      </Head>

      {/* Hero */}
      <div className="w-full border-b border-mf-gray-200 bg-mf-gray-50">
        <div className="mx-auto max-w-[768px] px-5 py-8 text-center lg:py-12">
          <div className="mb-3 text-[13px] font-semibold uppercase tracking-[0.15em] text-mf-blue">
            Legal
          </div>
          <h1 className="font-inter text-4xl font-extrabold tracking-[-0.02em] text-mf-off-black-950 lg:text-5xl">
            {frontmatter.title}
          </h1>
          <p className="mt-4 text-[15px] text-mf-gray-500">
            Last updated on {formatDate(frontmatter.lastUpdated, localeTag)} ·
            Version {frontmatter.version}
          </p>
        </div>
      </div>

      {/* Body */}
      <div className="mx-auto w-full max-w-[768px] px-5 py-14 lg:py-20">
        {toc.length > 1 && (
          <nav
            aria-label="On this page"
            className="mb-12 rounded-2xl border border-mf-gray-200 bg-mf-gray-50 p-5"
          >
            <div className="mb-3 text-[13px] font-semibold uppercase tracking-[0.12em] text-mf-gray-500">
              On this page
            </div>
            <ol className="space-y-1.5">
              {toc.map((item) => (
                <li key={item.id}>
                  <a
                    href={`#${item.id}`}
                    className="text-[14.5px] text-mf-blue hover:underline"
                  >
                    {item.text}
                  </a>
                </li>
              ))}
            </ol>
          </nav>
        )}

        <Prose html={html} />
      </div>
    </div>
  );
};
